import React from 'react'
import { Box, Button, Tooltip, Typography, useMediaQuery } from '@mui/material'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import PatientIdentityStatus from './PatientIdentityStatus'
import TokTokDocDialog from '../../../Common/Dialog'
import DowngradedIdentity from './DowngradedIdentity'
import { DowngradeIdentityDialogProps } from './_props'
import { useButtonsStyles } from '../../../../styles'
import idsTheme from '../../../../theme/idsTheme'
import TagList from '../../../Common/Tag/TagList'
import useUserPermissions from '../../../../hooks/useUserPermissions'
import sxs from './_styles/DowngradeIdentityDialogSxs'
import { useRegradeIdentity } from '../services/usePatientUtils'
import { useFormContextPatient } from '../_utils'
import { PatientIdentityStatusEnum } from '../_props'

const theme = idsTheme()

const downgradableFieldsLabels = [
    'Nom de naissance*',
    '1er prénom de naissance et autres prénoms de naissance*',
    'Date de naissance*',
    'Sexe*',
    'Pays de naissance*',
    'Ville de naissance (Code INSEE)',
]

const downgradableClearableFieldsLabels = ['Matricule INS*', 'OID']

const downgradeIdentitySlugsRealted: PatientIdentityStatusEnum[] = ['QUALIFIED', 'RECOVERED']

const DowngradeIdentityDialog: React.FC<DowngradeIdentityDialogProps> = ({
    isDowngraded,
    onDowngrade,
    currentIdentityStatusSlug,
}) => {
    const { watch } = useFormContextPatient()
    const watchedIdentityProof = watch('identityProof')
    const buttonStyles = useButtonsStyles()
    const { regradeIdentity } = useRegradeIdentity()
    const { hasUserPermission } = useUserPermissions()
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'))
    const [isDowngradeIdentityDialogShown, setIsDowngradeIdentityDialogShown] = React.useState(false)

    const isDowngradable = React.useMemo(
        () =>
            hasUserPermission('SUPER ADMIN') &&
            currentIdentityStatusSlug &&
            downgradeIdentitySlugsRealted.includes(currentIdentityStatusSlug),
        [currentIdentityStatusSlug, hasUserPermission],
    )
    const downgradeIdentitySlugTarget = regradeIdentity('downgrade', watchedIdentityProof?.value ?? 0)

    const handleDowngrade = React.useCallback(async () => {
        await onDowngrade()
        setIsDowngradeIdentityDialogShown(false)
    }, [onDowngrade])

    return (
        <>
            <Box>
                {isDowngradable && (
                    <Tooltip title="Rétrogradation de l'identité du patient">
                        <Button
                            className={buttonStyles.ButtonCancel}
                            variant="contained"
                            color="inherit"
                            disableElevation
                            startIcon={<LockOpenIcon />}
                            onClick={() => setIsDowngradeIdentityDialogShown(true)}
                        >
                            {isLargeScreen ? "Rétrograder l'identité" : ''}
                        </Button>
                    </Tooltip>
                )}
                <DowngradedIdentity displayed={isDowngraded} />
            </Box>

            <TokTokDocDialog
                isOpen={isDowngradeIdentityDialogShown}
                title="Rétrogradation de l'identité du patient"
                handleClose={() => setIsDowngradeIdentityDialogShown(false)}
                handleSave={handleDowngrade}
                saveTitle="Rétrograder l’identité"
            >
                <Box sx={sxs.PatientFormHeaderDialogContent}>
                    <Box sx={sxs.PatientFormHeaderDialogSection}>
                        <Typography>
                            En validant vous allez rétrograder l'identité du patient ce qui aura pour{' '}
                            <strong>conséquence</strong> de rendre <strong>disponible en édition</strong> les champs
                            suivants :
                        </Typography>
                        <TagList tags={downgradableFieldsLabels} />
                    </Box>
                    <Box sx={sxs.PatientFormHeaderDialogSection}>
                        <Typography>
                            Ainsi que de <strong>vider</strong> les champs suivants :
                        </Typography>
                        <TagList tags={downgradableClearableFieldsLabels} />
                    </Box>
                    <Box sx={sxs.PatientFormHeaderDialogSection}>
                        <Box sx={sxs.identityDowngradeInfo}>
                            <Typography>
                                De plus, l'identité du patient sera <strong>rétrogradée</strong> de{' '}
                            </Typography>
                            <Box sx={sxs.identityDowngradeInfoTags}>
                                <PatientIdentityStatus status={currentIdentityStatusSlug} display="tag" />
                                <Typography> à </Typography>
                                <PatientIdentityStatus status={downgradeIdentitySlugTarget} display="tag" />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </TokTokDocDialog>
        </>
    )
}

export default DowngradeIdentityDialog
